// LT-313 — Generic DataTable primitive per handoff §C.
//
// Usage:
//   <DataTable<Contact>
//     data={rows}
//     columns={cols}
//     getRowKey={(r) => r.id}
//     onRowClick={(r) => navigate(`/contacts/${r.id}`)}
//     enableSelection
//     renderRowActions={(r) => <ContactActions contact={r} />}
//     renderBulkActions={(selected) => <BulkBar leads={selected} />}
//   />
//
// Features (AC from LT-313):
//  1. Column config — `ColumnDef<T>` with header + accessor + optional
//     sortable + sortValue extractor.
//  2. Bulk selection — checkbox column when `enableSelection`, shift-click
//     for range, select-all-visible with indeterminate state.
//  3. Row menu — whatever `renderRowActions` returns; the table just gives
//     it a 3-dot-hover cell with focus management.
//  4. Keyboard — tab between rows (each row is focusable), Enter opens,
//     Space toggles selection on focused row, Shift+Click range, Cmd+A
//     selects all visible rows.
//  5. Bulk-action bar — sticky top banner appears when >=1 row selected;
//     shows count + `renderBulkActions(selected)`.
//  6. Scales to 1000+ rows via native overflow-auto + CSS
//     content-visibility: auto on body rows (no external virtualizer).
//
// A11y: role=table/row/columnheader/cell/rowheader. Checkboxes carry
// aria-labels. aria-sort on sortable headers. Focus ring on rows.
// Selection column is aria-hidden to screen readers where it'd duplicate
// the row's primary label (we keep the label on the row-opener element).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

export type SortDirection = 'asc' | 'desc';

export interface ColumnDef<T> {
  /** Stable identifier (used as React key and in aria-sort / sort state). */
  id: string;
  /** Display header. */
  header: React.ReactNode;
  /** Render the cell content for a given row. */
  accessor: (row: T) => React.ReactNode;
  /** When true, the header is a sort button. Default: false. */
  sortable?: boolean;
  /**
   * Comparator value for this column. Default: lowercased string of the
   * accessor output. Supply for numeric/date sort or to avoid re-rendering
   * to extract the comparable value.
   */
  sortValue?: (row: T) => string | number;
  /** Extra classes on the <th> / <td>. */
  className?: string;
  /** Right-align numeric columns etc. */
  align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Turn on the leading checkbox column + select-all + bulk bar wiring. */
  enableSelection?: boolean;
  /**
   * Current selection (lifted state). If omitted, DataTable manages selection
   * internally. Pass `selectedIds` + `onSelectionChange` to lift.
   */
  selectedIds?: Set<string>;
  onSelectionChange?: (nextSelectedIds: Set<string>) => void;
  /** Cell content for the trailing row-menu column. */
  renderRowActions?: (row: T) => React.ReactNode;
  /** Bulk action bar renderer — receives the array of selected rows. */
  renderBulkActions?: (selected: T[]) => React.ReactNode;
  /** What to render when `data` is empty. */
  emptyState?: React.ReactNode;
  /** Accessible description of the whole table. */
  ariaLabel?: string;
  /** Extra classes on the outer wrapper. */
  className?: string;
}

function defaultSortValue<T>(col: ColumnDef<T>, row: T): string | number {
  if (col.sortValue) return col.sortValue(row);
  const out = col.accessor(row);
  if (out == null) return '';
  if (typeof out === 'string' || typeof out === 'number') return out;
  // Best-effort stringify of ReactNode — good enough for JSX like <span>foo</span>.
  return String(out).toLowerCase();
}

export function DataTable<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  enableSelection = false,
  selectedIds: externalSelectedIds,
  onSelectionChange,
  renderRowActions,
  renderBulkActions,
  emptyState,
  ariaLabel,
  className,
}: DataTableProps<T>) {
  // Selection — lifted if a parent passed selectedIds, otherwise internal.
  const [internalSelected, setInternalSelected] = useState<Set<string>>(
    () => new Set(),
  );
  const selectedIds = externalSelectedIds ?? internalSelected;
  const setSelection = useCallback(
    (next: Set<string>) => {
      if (onSelectionChange) onSelectionChange(next);
      else setInternalSelected(next);
    },
    [onSelectionChange],
  );

  // Sorting — always internal. Caller can pre-sort `data` for server-sorted
  // lists (set every column `sortable: false`). null = native data order.
  const [sortBy, setSortBy] = useState<{ id: string; dir: SortDirection } | null>(null);

  const sortedData = useMemo(() => {
    if (!sortBy) return data;
    const col = columns.find((c) => c.id === sortBy.id);
    if (!col) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      const va = defaultSortValue(col, a);
      const vb = defaultSortValue(col, b);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return sortBy.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [data, columns, sortBy]);

  const visibleIds = useMemo(() => sortedData.map(getRowKey), [sortedData, getRowKey]);
  const visibleIdSet = useMemo(() => new Set(visibleIds), [visibleIds]);

  const selectedVisibleCount = useMemo(() => {
    let n = 0;
    selectedIds.forEach((id) => {
      if (visibleIdSet.has(id)) n++;
    });
    return n;
  }, [selectedIds, visibleIdSet]);

  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  // Tracks the last-clicked row key so Shift+Click ranges work.
  const lastClickedRef = useRef<string | null>(null);

  const toggleOne = useCallback(
    (id: string) => {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelection(next);
    },
    [selectedIds, setSelection],
  );

  const selectRange = useCallback(
    (fromId: string, toId: string) => {
      const fromIdx = visibleIds.indexOf(fromId);
      const toIdx = visibleIds.indexOf(toId);
      if (fromIdx < 0 || toIdx < 0) return;
      const [a, b] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      const next = new Set(selectedIds);
      for (let i = a; i <= b; i++) next.add(visibleIds[i]);
      setSelection(next);
    },
    [visibleIds, selectedIds, setSelection],
  );

  const selectAllVisible = useCallback(() => {
    const next = new Set(selectedIds);
    for (const id of visibleIds) next.add(id);
    setSelection(next);
  }, [visibleIds, selectedIds, setSelection]);

  const clearAllVisible = useCallback(() => {
    const next = new Set(selectedIds);
    for (const id of visibleIds) next.delete(id);
    setSelection(next);
  }, [visibleIds, selectedIds, setSelection]);

  const handleHeaderCheckbox = () => {
    if (allVisibleSelected) clearAllVisible();
    else selectAllVisible();
  };

  const handleRowCheckboxClick = (id: string, e: React.MouseEvent) => {
    if (e.shiftKey && lastClickedRef.current && lastClickedRef.current !== id) {
      selectRange(lastClickedRef.current, id);
    } else {
      toggleOne(id);
    }
    lastClickedRef.current = id;
  };

  const handleHeaderSort = (col: ColumnDef<T>) => {
    if (!col.sortable) return;
    setSortBy((prev) => {
      if (!prev || prev.id !== col.id) return { id: col.id, dir: 'asc' };
      if (prev.dir === 'asc') return { id: col.id, dir: 'desc' };
      return null;
    });
  };

  // Cmd/Ctrl+A select-all when focus is inside the table. Skips when typing
  // in an input / contenteditable so real Select-All still works.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!enableSelection) return;
    const root = rootRef.current;
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !root.contains(target)) return;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selectAllVisible();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enableSelection, selectAllVisible]);

  const handleRowKeyDown = (row: T, id: string) => (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    const target = e.target as HTMLElement;
    // Let interactive children (buttons, inputs, links) consume their own keys.
    if (target !== e.currentTarget) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      onRowClick?.(row);
    } else if (e.key === ' ' && enableSelection) {
      e.preventDefault();
      toggleOne(id);
    }
  };

  const handleRowClick = (row: T, id: string) => (e: React.MouseEvent<HTMLTableRowElement>) => {
    // Don't trigger the row-click when the user clicked an interactive child
    // (checkbox, button, link) — let that child handle its own click.
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, select, [role="menu"], [role="menuitem"]')) return;
    onRowClick?.(row);
  };

  const selectedRows = useMemo(
    () => sortedData.filter((r) => selectedIds.has(getRowKey(r))),
    [sortedData, selectedIds, getRowKey],
  );

  return (
    <div ref={rootRef} className={clsx('relative', className)}>
      {enableSelection && selectedRows.length > 0 && renderBulkActions ? (
        <div
          data-testid="datatable-bulk-bar"
          className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-accent-tint border-b border-accent-border px-3 py-2 text-sm"
        >
          <span className="text-accent-text font-medium">
            {selectedRows.length} selected
          </span>
          <div className="flex items-center gap-2">
            {renderBulkActions(selectedRows)}
            <button
              type="button"
              onClick={() => setSelection(new Set())}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-auto bg-surface-1 border border-white/[0.08] rounded-ds-xl">
        <table
          role="table"
          aria-label={ariaLabel}
          className="w-full text-sm border-collapse"
        >
          <thead className="bg-surface-2 text-left text-xs uppercase text-slate-400">
            <tr role="row">
              {enableSelection ? (
                <th scope="col" className="px-3 py-2 w-10" aria-label="Selection">
                  <input
                    type="checkbox"
                    aria-label={allVisibleSelected ? 'Deselect all visible rows' : 'Select all visible rows'}
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected;
                    }}
                    onChange={handleHeaderCheckbox}
                    className="h-4 w-4 rounded-ds-sm border border-white/[0.16] bg-surface-2 accent-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const isSorted = sortBy?.id === col.id;
                const ariaSort: React.AriaAttributes['aria-sort'] = isSorted
                  ? sortBy!.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : col.sortable
                    ? 'none'
                    : undefined;
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    className={clsx(
                      'px-3 py-2 font-medium',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.className,
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleHeaderSort(col)}
                        className="inline-flex items-center gap-1 uppercase tracking-wider text-xs text-slate-400 hover:text-slate-200 transition-colors"
                      >
                        <span>{col.header}</span>
                        <SortIndicator
                          isSorted={isSorted}
                          dir={isSorted ? sortBy!.dir : undefined}
                        />
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
              {renderRowActions ? (
                <th scope="col" className="px-3 py-2 w-12 text-right" aria-label="Actions" />
              ) : null}
            </tr>
          </thead>

          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (enableSelection ? 1 : 0) +
                    (renderRowActions ? 1 : 0)
                  }
                  className="px-3 py-8 text-center text-slate-400"
                >
                  {emptyState ?? 'No data'}
                </td>
              </tr>
            ) : (
              sortedData.map((row) => {
                const id = getRowKey(row);
                const isSelected = selectedIds.has(id);
                return (
                  <tr
                    key={id}
                    role="row"
                    tabIndex={0}
                    aria-selected={enableSelection ? isSelected : undefined}
                    onClick={handleRowClick(row, id)}
                    onKeyDown={handleRowKeyDown(row, id)}
                    className={clsx(
                      'border-t border-white/[0.08] transition-colors outline-none',
                      'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
                      isSelected ? 'bg-accent-tint' : 'hover:bg-surface-2',
                      onRowClick && 'cursor-pointer',
                    )}
                    data-selected={isSelected || undefined}
                  >
                    {enableSelection ? (
                      <td className="px-3 py-2 w-10">
                        <input
                          type="checkbox"
                          aria-label={isSelected ? `Deselect row` : `Select row`}
                          checked={isSelected}
                          onChange={() => {
                            // no-op; onClick handles the shift-click nuance
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowCheckboxClick(id, e);
                          }}
                          className="h-4 w-4 rounded-ds-sm border border-white/[0.16] bg-surface-2 accent-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        />
                      </td>
                    ) : null}
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={clsx(
                          'px-3 py-2 text-slate-200',
                          col.align === 'right' && 'text-right',
                          col.align === 'center' && 'text-center',
                          col.className,
                        )}
                      >
                        {col.accessor(row)}
                      </td>
                    ))}
                    {renderRowActions ? (
                      <td className="px-3 py-2 w-12 text-right" onClick={(e) => e.stopPropagation()}>
                        {renderRowActions(row)}
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SortIndicator: React.FC<{ isSorted: boolean; dir?: SortDirection }> = ({
  isSorted,
  dir,
}) => {
  if (!isSorted) {
    return (
      <span aria-hidden className="inline-block w-3 text-slate-500">
        ↕
      </span>
    );
  }
  return (
    <span aria-hidden className="inline-block w-3 text-accent">
      {dir === 'asc' ? '↑' : '↓'}
    </span>
  );
};

export default DataTable;
